import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/auth';

// Async thunks for authentication
export const loginWithEmail = createAsyncThunk(
  'auth/loginWithEmail',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (googleToken, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/auth/google', { token: googleToken });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Google login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password, name }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/auth/register', { email, password, name });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/api/auth/logout');
      return null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Checking auth status...');
      const response = await api.get('/api/auth/profile');
      console.log('Auth check successful:', response.data);
      return response.data;
    } catch (error) {
      console.log('Auth check failed:', error.response?.status, error.response?.data);
      // Don't treat 401 as an error - just means not authenticated
      if (error.response?.status === 401) {
        return rejectWithValue('Not authenticated');
      }
      // For other errors, also treat as not authenticated
      return rejectWithValue('Not authenticated');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('authToken') || null, // Get token from localStorage
    isAuthenticated: !!localStorage.getItem('authToken'), // Set to true if token exists
    loading: false, // Start with loading false
    error: null,
    authChecked: false, // Track if we've checked auth status
    initialLoad: true, // Track if this is the initial app load
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.authChecked = true;
      state.initialLoad = false;
      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    },
    setInitialLoadComplete: (state) => {
      state.initialLoad = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login with email
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.authChecked = true; // Mark as checked to prevent reload loop
        state.initialLoad = false; // Mark initial load as complete
        // Store token in localStorage
        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('refreshToken', action.payload.refreshToken);
        console.log('Email login successful, token stored in localStorage');
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.authChecked = true; // Mark as checked even on failure
      })
      // Google login
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.authChecked = true; // Mark as checked to prevent reload loop
        state.initialLoad = false; // Mark initial load as complete
        // Store token in localStorage
        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('refreshToken', action.payload.refreshToken);
        console.log('Google login successful, token stored in localStorage');
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.authChecked = true; // Mark as checked even on failure
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.authChecked = true; // Mark as checked to prevent reload loop
        state.initialLoad = false; // Mark initial load as complete
        // Store token in localStorage
        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('refreshToken', action.payload.refreshToken);
        console.log('Registration successful, token stored in localStorage');
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.authChecked = true; // Mark as checked even on failure
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        // Clear localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        console.log('Logout successful, tokens cleared from localStorage');
      })
      // Check auth status
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = localStorage.getItem('authToken'); // Get token from localStorage
        state.isAuthenticated = true;
        state.authChecked = true;
        state.initialLoad = false;
        console.log('Auth status check successful:', { user: action.payload.user, isAuthenticated: true });
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.authChecked = true;
        state.initialLoad = false;
        // Clear localStorage if auth check failed
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        console.log('Auth status check failed:', action.payload);
      });
  },
});

export const { clearError, clearAuth, setInitialLoadComplete } = authSlice.actions;
export default authSlice.reducer;
